import { css } from '@emotion/react';
import { tokens } from '@flysas-tech/commercial-ui';
import { t } from 'i18n';
import React, { useMemo, useState } from 'react';
import { BOUND_SIDE, FaresMap, Flight } from 'shared';

import { ChipSelector } from '../../ChipSelector';

import { createFlightRegistry, SortKey } from './helpers';

const styles = {
  sort: css`
    display: flex;
    justify-content: end;
    margin-bottom: ${tokens.dimension['space-6']};
    margin-top: ${tokens.dimension['space-6']};
    gap: ${tokens.dimension['space-3']};
  `,
  label: css`
    font-size: ${tokens.fontSize.paragraph['paragraph-2']};
    display: flex;
    align-items: center;
  `,
};

export function useFlightSort(flights: readonly Flight[], faresMap: FaresMap, boundSide: BOUND_SIDE) {
  const [value, setValue] = useState<SortKey>(SortKey.RECOMMENDED);

  const registry = useMemo(() => createFlightRegistry(faresMap), [faresMap]);

  const options = useMemo(
    () =>
      (Object.keys(registry) as SortKey[]).map(k => ({
        value: k,
        label: registry[k].label,
      })),
    [registry]
  );

  const sorted = useMemo(() => {
    return flights.toSorted(registry[value].compare);
  }, [flights, registry, value]);

  const Select: React.FC = () => {
    return (
      <div css={styles.sort}>
        <label css={styles.label} id="sort-label" htmlFor={`sort-dropdown-${boundSide}`} aria-hidden="true">
          {t('common:sortBy', { defaultValue: 'Sort by' })}
        </label>
        <ChipSelector<SortKey>
          id={`sort-dropdown-${boundSide}`}
          onChange={setValue}
          options={options}
          value={value}
          ariaLabel={t('a11y.sortFlightsBy', { defaultValue: 'Sort flight list by' })}
          showBorder={false}
        />
      </div>
    );
  };

  return { sorted, Select };
}
